import styled, { keyframes } from "styled-components";
import { InputNumber } from "antd";
import { SbTag } from "uiCore";
import { PageContainerStyled, TableWrapperStyled } from "styles/common.styles";

// ─── Page-level ────────────────────────────────────────────────────────────

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Wraps a `disabled` button before it goes inside an SbTooltip. A disabled
// antd Button doesn't cleanly fire the mouse events the Tooltip's hover
// trigger listens for, which can make the tooltip thrash trying to
// position itself (freezing the page on hover) — the standard fix is to
// give the Tooltip a plain element that DOES receive pointer events.
// $block matches a wrapped SbButton's own `block` prop — without it, an
// inline-block span shrinks to its content and a 100%-width button inside
// collapses against its own auto-sized parent instead of actually going
// full width.
export const TooltipTargetStyled = styled.span`
  display: ${(p) => (p.$block ? "block" : "inline-block")};
  width: ${(p) => (p.$block ? "100%" : "auto")};
`;

export const PageTransitionStyled = styled(PageContainerStyled)`
  animation: ${fadeSlideIn} 0.22s ease-out;
`;

// ─── PDF export (Download PDF, BUG #1940) ──────────────────────────────────
// Off-screen stage the full Current/Proposed trees are rendered into just
// long enough for utils/orgDesignPdf.jsx to capture them — always fully
// expanded and at 100% zoom, independent of whatever the user has collapsed,
// zoomed or toggled (Org View/Table View) on screen. Parked off-canvas rather
// than display:none/visibility:hidden because html2canvas needs real layout
// to measure and paint; `data-org-pdf-stage` is how the capture pulls its
// clone back on-canvas. Collapse carets are hidden — they're the only
// buttons a read-only node renders, and are meaningless on paper.
export const PdfCaptureStageStyled = styled.div.attrs({ "data-org-pdf-stage": true, "aria-hidden": true })`
  position: fixed;
  top: 0;
  left: -100000px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 48px;

  button {
    display: none;
  }
`;

export const ClickableTableWrapperStyled = styled(TableWrapperStyled)`
  padding: 0 24px;

  .ant-table-tbody > tr {
    cursor: pointer;
    transition: background-color 0.12s ease;
  }
  .ant-table-tbody > tr:hover > td {
    background: ${(p) => p.theme?.colors?.hoverBg ?? "#F5F7FA"} !important;
  }

  /* Same sticky-pagination format as PeoplePlusAIV2.styles.js's
     PAICTableWrapperStyled — stays visible at the bottom of whichever
     ancestor actually scrolls, instead of requiring a scroll past a long
     table just to page through it. */
  .ant-table-pagination {
    position: sticky;
    bottom: 0;
    z-index: 1;
    margin: 0 !important;
    padding: 12px 0;
    background: ${(p) => p.theme?.colors?.cardBg ?? p.theme?.colors?.white ?? "#fff"};
    border-top: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  }
`;

// Top-left "Back to X" link — shared by every Org Design page that sits a
// level below the landing page (Workspace, the PAIC import wizard).
export const BackLinkStyled = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
`;

// Generic muted/secondary caption text — smaller than antd Typography.Text's
// default body size, reused wherever a hint/empty-state line needs it.
export const MutedCaptionStyled = styled.span`
  font-size: 12.5px;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
`;

// Marks a table column header as a hard gate the row must pass before the
// user can proceed (e.g. "Min. Role Count *") — paired with a
// "* Required to..." caption near the table so the asterisk's meaning is
// spelled out once rather than left to guesswork.
export const RequiredMarkStyled = styled.span`
  color: #dc2626;
  margin-left: 3px;
`;

export const RequiredCaptionRowStyled = styled.div`
  padding: 8px 24px 0;
`;

// ─── Landing page ──────────────────────────────────────────────────────────

export const EntryCardsRowStyled = styled.div`
  display: flex;
  gap: 10px;
  padding: 4px 24px;
`;

export const EntryCardStyled = styled.div`
  flex: 1;
  border: 1px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  border-radius: 8px;
  padding: 10px 12px;
  cursor: ${(p) => (p.$loading ? "default" : "pointer")};
  opacity: ${(p) => (p.$loading ? 0.6 : 1)};
  pointer-events: ${(p) => (p.$loading ? "none" : "auto")};
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 6px;

  .entry-title {
    font-size: 13px;
    font-weight: 700;
  }
  // min-height reserves room for 2 lines up front — the loading message
  // ("Loading your organization structure…") swapped in on click is
  // shorter than the normal description, and without this the card (and
  // everything below it, since both entry cards stretch to match each
  // other's height) visibly shrinks and jumps up the moment loading starts.
  .entry-desc {
    font-size: 11.5px;
    color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
    min-height: 2.2em;
  }

  &:hover {
    border-color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

// Fixed height for the icon/spinner slot at the top of an entry card — the
// normal icon and the loading SbSpin render at different natural sizes, and
// without a shared slot height swapping between them shifts the title/desc
// below it up or down.
export const EntryIconSlotStyled = styled.div`
  min-height: 28px;
  display: flex;
  align-items: center;
`;

// Rounded, tinted badge behind an entry card's icon — a plain floating
// glyph read as a bit bare for a landing page meant to feel like a proper
// dashboard; a colored badge is the more typical "pick one of these paths"
// card treatment.
export const EntryIconBadgeStyled = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.theme?.colors?.primaryLight ?? "rgba(0, 128, 128, 0.1)"};

  .anticon {
    font-size: 15px;
    color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
  }
`;

// Landing table's Project Name cell — sb_org_redesign_code has no column of
// its own (the requested column order has no room for it), so it renders as
// a muted second line under the name instead, the same "primary + caption"
// stack the rest of the app uses for a record's human name plus its code.
export const ProjectNameCellStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .code-text {
    font-size: 11px;
    color: ${(p) => p.theme?.colors?.textSecondary ?? "#8C97A8"};
  }
`;

// $sectionGap is a bigger top gap than $topGap — used above "Previous
// Iterations" so the published-versions table reads as its own section
// instead of crowding straight into the entry cards above it (BUG #1800).
export const SectionTitleRowStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(p) => (p.$sectionGap ? "28px 24px 2px" : p.$topGap ? "6px 24px 2px" : "0 24px 2px")};

  .ant-typography {
    margin: 0;
  }
`;

// ─── PAIC import wizard — group/version list, full-page like RoleReporting ─
// Steps 1/2 are data tables, so they fill the page edge-to-edge the same way
// RoleReporting/CompensationManagement do (PageContainer flex column + a
// flex:1 content area) instead of sitting in a small margined card with dead
// space below — same page shell (PageTransitionStyled/PageHeaderStyled),
// same "flex:1; min-height:0; overflow:hidden" content convention.

export const ImportStepsBarStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-bottom: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  flex-shrink: 0;
`;

// A step is only clickable once it's already been completed — jumping ahead
// to a step whose selection (group/version) doesn't exist yet would leave
// that screen with nothing to render.
export const StepTagStyled = styled(SbTag)`
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
`;

// Both wizard steps are a single flat table now — no tabs — matching the
// EXACT proven pattern from PeoplePlusAIV2.styles.js's TableSection/
// PAICTableWrapperStyled (the live PAIC dashboard) and JobsLibraryV2's
// PageTableWrapperStyled: one real scroll container (flex:1,
// overflow-y:auto), one inner wrapper for the sticky pagination footer, and
// `sticky` on <SbTable> itself for the pinned header. An earlier tabbed
// version ("My PAIC Groups" / "Delegated to Me" as separate antd Tabs) kept
// breaking scrolling — chaining flex heights through antd Tabs' own several
// layers of internal wrapper divs never reliably resolved. Dropping tabs
// for a single table with an Owner/Delegated badge column sidesteps that
// entirely, and reuses a pattern already proven twice elsewhere in this app.
export const ImportSectionStyled = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;

export const ImportSectionHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 24px;
  border-bottom: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  font-size: 14px;
  font-weight: 700;
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${(p) => p.theme?.colors?.cardBg ?? "#fff"};

  .section-count {
    font-weight: 400;
    color: ${(p) => p.theme?.colors?.textSecondary ?? "#8C97A8"};
    font-size: 12.5px;
  }
`;

// Table + sticky pagination footer, no side padding so the table itself
// sits flush edge-to-edge. Every row here is clickable (proceeds to the
// next step) unless marked `row-not-ready` (a group that hasn't cleared its
// Min 5 Met / Reporting / Readiness gate) — muted and not-allowed instead of
// the normal pointer/hover treatment, so that reads as blocked before the
// user even clicks it.
export const ImportTableInnerStyled = styled.div`
  .ant-table-pagination {
    position: sticky;
    bottom: 0;
    background: ${(p) => p.theme?.colors?.cardBg ?? "#fff"};
    padding: 12px 24px;
    margin: 0 !important;
    border-top: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  }

  /* antd only forces header titles onto a single line when the column also
     sets the ellipsis option — none of these columns do, so without this a
     header wider than its (narrow, filterable) column wraps to two lines.
     Widths are sized generously enough below that this never clips text. */
  .ant-table-column-title {
    white-space: nowrap;
  }
  /* antd's default is space-between, which pins the filter funnel icon to
     the column's far (right) edge regardless of how short the header label
     is, leaving a large, disconnected-looking gap. flex-start keeps the
     icon immediately next to its label instead. */
  .ant-table-filter-column,
  .ant-table-column-sorters {
    justify-content: flex-start;
    gap: 6px;
  }

  .ant-table-tbody > tr {
    cursor: pointer;
    transition: background-color 0.12s ease;
  }
  .ant-table-tbody > tr:hover > td {
    background: ${(p) => p.theme?.colors?.hoverBg ?? "#F5F7FA"} !important;
  }
  /* A visible grey fill (not just opacity, which also muddies the red/green
     status tags' contrast) is what actually communicates "blocked" at a
     glance — !important on both base and hover states since antd's own
     td/hover rules would otherwise win on specificity/insertion order. */
  .ant-table-tbody > tr.row-not-ready {
    cursor: not-allowed;
  }
  .ant-table-tbody > tr.row-not-ready > td {
    background: ${(p) => p.theme?.colors?.subtleBg ?? "#F1F2F4"} !important;
  }
  .ant-table-tbody > tr.row-not-ready:hover > td {
    background: ${(p) => p.theme?.colors?.subtleBg ?? "#F1F2F4"} !important;
  }
`;

export const ImportEmptyStyled = styled.div`
  padding: 32px 18px;
  text-align: center;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#8C97A8"};
  font-size: 12.5px;
`;

// Expanded-row roles preview — GET /paic-projects embeds each group's
// roles[] inline (doc §2 Field Notes), so expanding a group row previews
// them with no extra API call. Row click still proceeds to Step 2; the
// expand toggle (antd's own leftmost caret) is a separate control.
export const ExpandedRolesPanelStyled = styled.div`
  margin: 4px 8px 4px 40px;
  padding: 12px 14px;
  border: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  border-radius: 8px;
  background: ${(p) => p.theme?.colors?.subtleBg ?? "#FAFBFC"};
`;

export const ExpandedRolesHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  .roles-title {
    font-size: 12.5px;
    font-weight: 700;
    color: ${(p) => p.theme?.colors?.text ?? "#1F2A37"};
  }
  .roles-hint {
    font-size: 11.5px;
    color: ${(p) => p.theme?.colors?.textSecondary ?? "#8C97A8"};
  }
`;

// A flex row's text child doesn't truncate on its own — flex items default
// to min-width:auto, which refuses to shrink below the text's natural
// width, so ellipsis never kicks in without min-width:0 here AND on the
// text span itself (used with column.ellipsis for the long, auto-generated
// PAIC group name slugs).
export const GroupNameCellStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-weight: 600;
  color: ${(p) => p.theme?.colors?.text ?? "#1F2A37"};

  .anticon {
    flex-shrink: 0;
    color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
    font-size: 15px;
  }

  .name-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const RowChevronStyled = styled.span`
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#C2C9D6"};
  font-size: 13px;
  display: flex;
  justify-content: flex-end;
`;

// A group row's expand toggle — down arrow, rotates to point up when
// expanded. Its own onClick stops propagation so toggling it never also
// triggers the row's "proceed to Step 2" click handler.
export const ExpandToggleStyled = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#8C97A8"};
  cursor: pointer;
  transition: background-color 0.12s ease;

  .anticon {
    transition: transform 0.15s ease;
    transform: rotate(${(p) => (p.$expanded ? 180 : 0)}deg);
  }

  &:hover {
    background: ${(p) => p.theme?.colors?.borderLight ?? "#F0F1F5"};
    color: ${(p) => p.theme?.colors?.primary ?? "#008080"};
  }
`;

// Step 3's scroll container. Steps 1 and 2 get theirs from
// ImportSectionStyled; step 3 renders a plain card, and with no scroll area
// of its own its content — the summary bridge, both tree panels and the
// Open in Workspace/Regenerate buttons — simply overflowed the page shell.
// PageContainerStyled is height:100% + overflow:hidden, so everything past
// the fold was clipped with no way to reach it, which is the "half the page
// is stuck and cut" report.
//
// Same three rules as ImportSectionStyled. `min-height: 0` is the load-
// bearing one: without it a flex item's automatic minimum size keeps it at
// its content's full height, so it pushes out of the container instead of
// scrolling inside it.
export const PreviewScrollAreaStyled = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;

export const PreviewCardStyled = styled.div`
  margin: 20px 24px;
  border: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  border-radius: 10px;
  padding: 24px;
  background: ${(p) => p.theme?.colors?.cardBg ?? "#fff"};

  .ant-typography:first-child {
    margin-bottom: 4px;
  }
`;

export const PreviewLoadingRowStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
`;

// Wraps the Generate/Open-Existing action(s) below the gate check — a
// single button or a button + hint line, both spaced the same from the
// text above.
export const PreviewActionsStyled = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

export const PreviewButtonRowStyled = styled.div`
  display: flex;
  gap: 8px;
`;

// Inline "generated result" preview on the PAIC import wizard's Preview &
// Generate step — a read-only headcount bridge + org tree shown right below
// the Generate button, before the user commits to opening the full editable
// workspace.
export const PreviewResultStyled = styled.div`
  margin-top: 20px;
  border-top: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  padding-top: 16px;
`;

export const PreviewResultHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.theme?.colors?.success ?? "#16A34A"};
  margin-bottom: 8px;

  .anticon {
    font-size: 15px;
  }
`;

export const TreePreviewStyled = styled.div.attrs({ "data-org-scroll-area": true })`
  overflow: auto;
  max-height: 420px;
  padding: 16px 4px;
  border: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  border-radius: 8px;
  background: #fafbfc;
`;

export const GateBannerStyled = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.5;
  border: 1px solid ${(p) => (p.$tone === "error" ? "#FCA5A5" : "#FDE68A")};
  background: ${(p) => (p.$tone === "error" ? "#FEF2F2" : "#FFFBEB")};
  color: ${(p) => (p.$tone === "error" ? "#7F1D1D" : "#78350F")};

  .anticon {
    margin-top: 2px;
    font-size: 15px;
  }
`;

// ─── Workspace toolbar ─────────────────────────────────────────────────────

export const WorkspaceToolbarStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 24px;
  border-bottom: 1px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  flex-wrap: wrap;
`;

export const ToolbarActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// ─── Org tree (forked from TalentMobilityV2/SuccessionPlanningV2.styles.js —
// generic CSS nested-<ul>/<li> centered org-chart layout, not TM-specific) ──

// `overflow: auto` gives a large tree a real, familiar scrollbar (plus
// native wheel/trackpad scroll) instead of requiring click-drag as the only
// way to see the rest of it. Centering is done on TreeContainerStyled via
// `margin: 0 auto` instead of `justify-content: center` here — a flex
// container can only scroll its overflow in the positive direction, so a
// centered child wider than the viewport has its left half permanently
// unreachable. `margin: auto` degrades to 0 once the child is wider than
// its container, leaving it flush left and fully scrollable both ways.
// `$panning` drives the grab/grabbing cursor for click-and-drag panning
// (see OrgTreeCard's handlePanStart), which now just moves this same
// native scroll position rather than a separate transform offset.
// `user-select: none` stops a fast drag from also highlighting the tree's
// text as it moves.
// `data-org-scroll-area` is how OrgDesignRoleBoxNode's collapse-toggle
// handler finds its nearest scrollable ancestor (via `.closest()`) to
// correct for the scroll-position jump described in bug #1910 — baked in
// here via `.attrs()`, not added per call site, so every scroll container
// that can render a collapsible tree node gets it automatically, including
// TreePreviewStyled below.
export const ChartScrollAreaStyled = styled.div.attrs({ "data-org-scroll-area": true })`
  flex-grow: 1;
  // Without this, a flex child's height defaults to its content's natural
  // size instead of the space flex-grow actually gives it — the tree just
  // grows taller instead of scrolling vertically.
  min-height: 0;
  overflow: auto;
  position: relative;
  background: #fff;
  cursor: ${(p) => (p.$panning ? "grabbing" : "grab")};
  user-select: none;
`;

export const TreeContainerStyled = styled.div`
  padding: 16px 24px;
  background: #fff;
  width: max-content;
  margin: 0 auto;
  transform: scale(${(p) => p.$zoom ?? 1});
  transform-origin: top center;
  transition: transform 0.15s ease;
`;

export const ZoomControlsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ZoomLevelStyled = styled.span`
  font-size: 12px;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
  min-width: 38px;
  text-align: center;
`;

// Role Change legend (components/RoleChangeLegend.jsx) — sits once above
// both tree cards, not per-card, since it explains a status vocabulary
// shared by every node in both trees.
export const LegendRowStyled = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 10px 24px 0;
`;

export const LegendLabelStyled = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme?.colors?.textSecondary ?? "#5A6A7A"};
`;

export const LegendItemStyled = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${(p) => p.theme?.colors?.dark ?? "#2B3034"};
`;

// `$accentToken` is a theme.colors key (see getRoleChangeConfig,
// orgDesign.constants.js) — same brand token driving the matching node's
// border accent and status tag, so the legend's dot always agrees with what
// it's explaining rather than introducing its own color.
export const LegendDotStyled = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => p.theme?.colors?.[p.$accentToken] ?? p.theme?.colors?.tertiary ?? "#8790A4"};
`;

// Current and Proposed as two fully independent cards — each gets its own
// zoom/pan/export (see components/OrgTreeCard.jsx), not one shared canvas,
// so zooming or panning one side never affects the other.
export const OrgTreeCardsRowStyled = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 8px;
  padding: 16px 12px;
`;

export const OrgTreeCardStyled = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${(p) => p.theme?.colors?.border ?? "#D8DAE5"};
  border-radius: 10px;
  overflow: hidden;
  background: #fff;

  // Same "expand to fill the app's main content area" pattern as
  // DocumentView.styles.js's PageContainer — not true OS fullscreen, just
  // covers the space to the right of the sidebar/below the top bar.
  ${(p) =>
    p.$isFullscreen &&
    `
    position: fixed;
    top: 80px;
    left: 100px;
    right: 24px;
    bottom: 24px;
    z-index: 1000;
    border-radius: 8px;
    margin: 0;
  `}
`;

export const OrgTreeCardHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
  flex-shrink: 0;
`;

const CONNECTOR = "#D8DAE5";
const CONNECTOR_GAP = 28;

export const OrgChartListStyled = styled.ul`
  display: flex;
  justify-content: center;
  list-style: none;
  margin: ${(p) => (p.$topGap ? "12px 0 0" : "0")};
  padding: 0;

  ul {
    display: flex;
    justify-content: center;
    list-style: none;
    margin: 0;
    padding-top: ${CONNECTOR_GAP}px;
    position: relative;
  }

  li {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    padding: ${CONNECTOR_GAP}px 12px 0;
  }

  li::before,
  li::after {
    content: "";
    position: absolute;
    top: 0;
    width: 50%;
    height: ${CONNECTOR_GAP}px;
    border-top: 1px solid ${CONNECTOR};
  }
  li::before { right: 50%; }
  li::after { left: 50%; border-left: 1px solid ${CONNECTOR}; }

  li:only-child { padding-top: 0; }
  li:only-child::before,
  li:only-child::after { display: none; }

  li:first-child::before,
  li:last-child::after { border: none; }
  li:last-child::before { border-right: 1px solid ${CONNECTOR}; border-radius: 0 6px 0 0; }
  li:first-child::after { border-radius: 6px 0 0 0; }

  ul::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    width: 0;
    height: ${CONNECTOR_GAP}px;
    border-left: 1px solid ${CONNECTOR};
  }

  & > li { padding-top: 0; }
  & > li::before,
  & > li::after { display: none; }
`;

// ─── Table view (workspace's alternate flat-table representation) ─────────

export const RoleTableWrapperStyled = styled(TableWrapperStyled)`
  padding: 0 0 16px;

  /* Row hover — the row itself isn't clickable (every cell is its own
     inline-editable control), just a visual read cue like every other
     table in the app. */
  .ant-table-tbody > tr:hover > td {
    background: ${(p) => p.theme?.colors?.hoverBg ?? "#F5F7FA"} !important;
  }
`;

export const ProposedRolesHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0 8px;
  padding: 0 24px;
`;

// Compare modal, and the Generate preview's inline before/after — trees
// side by side.
export const CompareRowStyled = styled.div`
  display: flex;
  gap: 16px;
`;

export const CompareColumnStyled = styled.div`
  flex: 1;
  overflow: auto;
  max-height: 480px;
`;

// Small colored label above a comparison column — "Current" muted/neutral,
// "Proposed" in the primary color — so which side is which reads at a
// glance instead of relying on left-to-right position alone.
export const CompareColumnHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${(p) => (p.$variant === "proposed" ? p.theme?.colors?.primary ?? "#008080" : p.theme?.colors?.textSecondary ?? "#5A6A7A")};

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => (p.$variant === "proposed" ? p.theme?.colors?.primary ?? "#008080" : "#C2C9D6")};
  }
`;

// Sits between the two independent tree cards (see OrgTreeCard.jsx) as a
// third flex child — `align-self: stretch`
// overrides the row's own `align-items: flex-start` for just this one
// element, so the line spans the taller of the two trees' full height
// regardless of how the columns themselves are aligned.
export const TreeDividerStyled = styled.div`
  align-self: stretch;
  width: 1px;
  background: ${(p) => p.theme?.colors?.borderLight ?? "#E5E7EB"};
`;

// Modal form fields — a field followed by another needing breathing room
// before it, without putting margin on the input itself.
export const ModalFieldStyled = styled.div`
  margin-bottom: 12px;

  .ant-select, .ant-input-number {
    width: 100%;
  }
`;

// Fixed-width numeric inputs — the "HC" column's per-row field (needs to
// stay compact inside the table cell) vs. the Add Role modal's headcount/
// budget fields (each should fill the modal's width).
export const FullWidthInputNumberStyled = styled(InputNumber)`
  width: 100%;
`;

// Same-look InputNumber at an arbitrary fixed width — used wherever a field
// needs a specific px width that isn't the "narrow" (64px) or "full" (100%)
// case above (Budget Bar's inline editor, Org View's add-child popover).
export const SizedInputNumberStyled = styled(InputNumber)`
  width: ${(p) => p.$width ?? 100}px;
`;

